import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import {
  INormalizedLmcDataType,
  lmcOptionComponentProps,
  volumeCardFormProps,
} from "./types";
import LmcUtil from "./util";
import { HiOutlineTrash } from "react-icons/hi";
import LMCButtons from "../../../public/LMCButtons";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";

const createSchema = (t: any) =>
  Joi.object()
    .keys({
      values: Joi.array()
        .items(
          Joi.object({
            value: Joi.number()
              .positive()
              .min(1)
              .max(100)
              .allow(null)
              .allow("")
              .optional()
              .label(
                t("dashboard.features.lmc.volumeCard.validation.defaultAmount")
              ),
          })
        )
        .unique()
        .min(1)
        .max(3)
        .custom((value, helper) => {
          if (!LmcUtil.uniqueValues(value, "value")) {
            return helper.message(
              t(
                "dashboard.features.lmc.volumeCard.validation.uniqueAmounts"
              ) as any
            );
          }
          return value;
        })
        .custom((value, helper) => {
          if (!LmcUtil.atLeastOneItemIsFilled(value)) {
            return helper.message(
              t(
                "dashboard.features.lmc.volumeCard.validation.atLeastOneAmount"
              ) as any
            );
          }
          return value;
        })
        .required()
        .label(t("dashboard.features.lmc.volumeCard.validation.defaultAmounts"))
        .messages({
          "array.includesRequiredKnowns": t(
            "dashboard.features.lmc.volumeCard.validation.addOneValue"
          ),
        }),
      defaultValue: Joi.string()
        .optional()
        .label(t("dashboard.features.lmc.volumeCard.validation.defaultValue")),
      unit: Joi.string()
        .pattern(new RegExp(/^[a-zA-Z\s]+$/))
        .required()
        .label(t("dashboard.features.lmc.volumeCard.validation.unit"))
        .max(20)
        .messages({
          "string.pattern.base": t(
            "dashboard.features.lmc.volumeCard.validation.onlyAlphabets"
          ),
        }),
      comment: Joi.string()
        .max(100)
        .optional()
        .allow("", null)
        .label(t("dashboard.features.lmc.volumeCard.validation.comment"))
        .messages({
          "string.max": t(
            "dashboard.features.lmc.incrementalCard.validation.commentMax"
          ),
        }),
      customValue: Joi.boolean()
        .required()
        .label(t("dashboard.features.lmc.volumeCard.validation.customValue")),
    })
    .required();

const VolumeCard = ({
  label,
  optionData,
  onSubmitCardData,
  playgroundDataHandler,
  handleCancelEdit,
  tutorial,
}: lmcOptionComponentProps) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
    trigger,
  } = useForm<volumeCardFormProps>({
    resolver: joiResolver(createSchema(t)),
    mode: "onSubmit",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "values",
  });

  useEffect(() => {
    if (optionData) {
      let denormalizedData = LmcUtil.unwrapper(
        "VOLUME_CARD",
        optionData
      ) as volumeCardFormProps;

      reset({ ...denormalizedData });
    }
  }, [optionData, reset]);

  const onSubmit = (data: volumeCardFormProps) => {
    const newData = {
      ...data,
      values: data.values
        .filter((item: any) => item.value !== null)
        .filter((item: any) => item.value !== "")
        .map((v: { value: any }) => v.value),
    };

    const normalizedData = LmcUtil.wrapper(
      "VOLUME_CARD",
      newData
    ) as INormalizedLmcDataType;

    onSubmitCardData("VOLUME_CARD", normalizedData).then(() => {
      reset();
    });
  };

  useEffect(() => {
    watch((value, { name, type }) => {
      const newData = {
        ...value,
        values: value?.values
          ?.filter((item: any) => item.value !== null)
          ?.filter((item: any) => item.value !== "")
          ?.map((v) => v?.value),
      };

      playgroundDataHandler({
        type: "VOLUME_CARD",
        data: LmcUtil.wrapper("VOLUME_CARD", newData) as INormalizedLmcDataType,
      });
    });
  }, [playgroundDataHandler, watch]);

  return (
    <div className="lmc-card point-card w-full">
      <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-6">
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <div className="mb-5">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-7 h-7 bg-blue-500 text-white rounded-lg text-sm font-semibold shadow-sm">
                1
              </span>
              {t("dashboard.features.lmc.volumeCard.section1Title")}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {t("dashboard.features.lmc.volumeCard.section1Desc")}
            </p>
          </div>

          <div className="space-y-4">
            <CustomInput
              name="unit"
              control={control}
              label={t("dashboard.features.lmc.volumeCard.label3") as string}
              error={errors.unit}
              maxLength={20}
              placeholder={
                t("dashboard.features.lmc.volumeCard.placeholder3") as string
              }
              helperText={errors.unit?.message}
            />

            <div className="space-y-3">
              {fields.map((item, index) => (
                <div key={item.id}>
                  <CustomInput
                    control={control}
                    name={`values.${index}.value`}
                    label={
                      t("dashboard.features.lmc.volumeCard.label1") as string
                    }
                    className={`${
                      fields.length > 1 ? "col-span-6" : "col-span-7"
                    }`}
                    helperText={errors.values?.[index]?.value?.message}
                    error={errors.values?.[index]?.value}
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    min={1}
                    max={30}
                    placeholder={
                      t(
                        "dashboard.features.lmc.volumeCard.placeholder1"
                      ) as string
                    }
                    onValueChange={() => {
                      trigger();
                    }}
                    rightComponent={
                      <div className="flex items-center gap-2">
                        <div
                          className="text-gray-500 text-sm font-medium"
                          translate="no"
                        >
                          {watch("unit") || ""}
                        </div>
                        {fields.length > 1 && (
                          <div className="col-span-1 flex self-stretch items-center">
                            <CustomButton
                              variant="none"
                              size="icon"
                              className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                              onClick={() => remove(index)}
                              icon={<HiOutlineTrash size={18} />}
                            />
                          </div>
                        )}
                      </div>
                    }
                  />
                </div>
              ))}

              {watch("values")?.length < 3 && (
                <CustomButton
                  onClick={() => append({ value: null })}
                  className="w-full bg-white border-2 border-dashed border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-all"
                  title={
                    watch("values")?.length === 0
                      ? t("dashboard.features.lmc.volumeCard.key1First")
                      : t("dashboard.features.lmc.volumeCard.key1")
                  }
                />
              )}

              {!!errors.values && (
                <p className="text-red-600 text-sm font-medium bg-red-50 border border-red-200 rounded-lg p-3">
                  {errors.values?.message?.toString()}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <label className="switch shrink-0 mt-0.5">
                <input type="checkbox" {...register("customValue")} />
                <div className="slider"></div>
              </label>
              <div className="flex-1">
                <span className="text-gray-900 text-sm font-semibold block mb-1">
                  {t("dashboard.features.lmc.volumeCard.customValueTitle")}
                </span>
                <span className="text-gray-700 text-xs leading-relaxed">
                  {t("dashboard.features.lmc.volumeCard.label5")}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <div className="mb-5">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-7 h-7 bg-blue-500 text-white rounded-lg text-sm font-semibold shadow-sm">
                2
              </span>
              {t("dashboard.features.lmc.volumeCard.section2Title")}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {t("dashboard.features.lmc.volumeCard.section2Desc")}
            </p>
          </div>

          <div className="space-y-4">
            <CustomInput
              rows={4}
              name="comment"
              control={control}
              error={errors.comment}
              helperText={errors.comment?.message}
              placeholder={
                t("dashboard.features.lmc.volumeCard.placeholder4") as string
              }
              label={t("dashboard.features.lmc.volumeCard.label4") as string}
            />
          </div>
        </div>

        <div className="pt-2">
          <LMCButtons tutorial={tutorial} handleCancelEdit={handleCancelEdit} />
        </div>
      </form>
    </div>
  );
};

export const VolumeCardUi = ({
  data,
}: {
  data: INormalizedLmcDataType | null;
}) => {
  const { t } = useTranslation();

  return (
    <div className="self-stretch  flex-col justify-start items-center gap-6 flex">
      <div className="self-stretch  bg-gray-800 rounded-xl shadow flex-col justify-start items-center gap-6 flex">
        <div className="self-stretch px-4 pb-5 flex-col justify-center items-center gap-3 flex pt-4">
          <div className="text-center text-gray-400 text-base font-bold font-figtree leading-normal">
            {t("common.add")} <span translate="no">{data?.options?.unit}</span>
          </div>
          {data?.options?.custom_value && (
            <div className="self-stretch h-11 flex-col justify-center items-center gap-4 flex">
              <div className="self-stretch h-11 bg-gray-700 rounded-full flex-col justify-center items-center gap-2 flex">
                <div className="self-stretch grow shrink basis-0 px-6 py-2.5 justify-center items-center gap-2 inline-flex">
                  <div className="text-center text-gray-200 text-base font-semibold font-figtree leading-normal">
                    {t("dashboard.features.lmc.volumeCard.key5")}
                  </div>
                </div>
              </div>
            </div>
          )}
          {data?.pre_defined_value?.values?.map((v, index) => {
            if (v > 0 && v <= 100)
              return (
                <div
                  key={index}
                  className="self-stretch h-11 flex-col justify-center items-center gap-4 flex"
                >
                  <div className="self-stretch h-11 bg-gray-700 rounded-full flex-col justify-center items-center gap-2 flex">
                    <div className="self-stretch grow shrink basis-0 px-6 py-2.5 justify-center items-center gap-2 inline-flex">
                      <div className="text-center text-gray-200 text-base font-semibold font-figtree leading-normal">
                        +{Math.abs(+v)}{" "}
                        <span translate="no">{data.options?.unit}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            else return null;
          })}
        </div>
      </div>
    </div>
  );
};

export default VolumeCard;
